import React, { useContext } from 'react'

import { withRouter, RouteComponentProps } from 'react-router-dom'
import { Header, Segment, Form, CheckboxProps, Message, Modal, Button } from 'semantic-ui-react'

import CustomLabel from '../../../components/common/custom-label/CustomLabel'

import { IProject } from '../../../api/Data'
import { maxLength } from '../../../config/constants'
import { UserContext } from '../../../contexts/UserContext'

import CollaborateTabContestLinks from './CollaborateTabContestLinks'

const GalleryLegalStatusRow = ({ label, enabled }: { label: string; enabled: boolean }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: '0.75rem',
      marginBottom: '0.35rem',
      fontSize: '12px',
    }}
  >
    <span>{label}</span>
    <strong>{enabled ? '✓' : '✘'}</strong>
  </div>
)

const CollaborateTabContestPage = (props: IProps) => {
  // Props
  const { project, validationError, handleChange } = props
  const { userProfile } = useContext(UserContext)
  const [showLegalWarning, setShowLegalWarning] = React.useState(false)

  const {
    enableContest,
    contestDescription,
    contestContactEmail,
    contestVotingUse,
    contestVotingRules,
    contestCommentingPolicy,
    contestModerationPolicy,
    teamTermsUrl,
  } = project

  const projectWithLegal = project as any

  const legalEntity = projectWithLegal.overrideContentOwner || projectWithLegal.contentOwner
  const privacyPolicyUrl = projectWithLegal.overridePrivacyUrl || projectWithLegal.controllerPrivacyUrl
  const legalDisclosureUrl = projectWithLegal.overrideLegalDisclosureUrl || projectWithLegal.legalDisclosureUrl

  const hasLegalEntity = !!legalEntity
  const hasPrivacyPolicy = !!privacyPolicyUrl
  const hasLegalDisclosure = !!legalDisclosureUrl
  const hasGalleryTerms = !!teamTermsUrl
  const defaultContestContactEmail = (userProfile as any)?.email || (userProfile as any)?.username || ''
  const effectiveContestContactEmail = contestContactEmail || defaultContestContactEmail
  const hasReportOrTakedownRoute = !!effectiveContestContactEmail
  const canPublishGallery = hasLegalEntity && hasPrivacyPolicy

  const handleCheckboxChange = (e: any, data: CheckboxProps) => {
    const { name, checked } = data

    if (name === 'enableContest' && checked && !canPublishGallery) {
      setShowLegalWarning(true)
      return
    }

    handleChange(e, {
      name: String(name),
      checked: Boolean(checked),
    })
  }

  const handleLegalFieldChange = (e: any, data: any) => {
    handleChange(e, {
      name: 'useTeamLegalSettings',
      value: false,
    })

    handleChange(e, data)
  }

  return (
    <>
      <Header as="h1" style={{ marginBottom: '2rem', fontWeight: 'bold' }}>
        Public Video Gallery
        <Header.Subheader>Turn shortlisted entries into a public showcase people can vote on.</Header.Subheader>
        <Header.Subheader>
          Publish approved videos, share one gallery link, and invite your audience to{' '}
          <strong>vote, like and comment</strong>. Keep it fun for viewers while keeping the basics covered: privacy,
          legal identity and takedown route.
        </Header.Subheader>
      </Header>

      <style>
        {`
          .contest-gallery-form input::placeholder,
          .contest-gallery-form textarea::placeholder {
            color: #444444 !important;
            opacity: 1 !important;
          }

          .contest-gallery-form .default.text {
            color: #444444 !important;
            opacity: 1 !important;
          }
        `}
      </style>

      <Segment>
        <Form className="contest-gallery-form">
          <Form.Field>
            <CustomLabel>
              <CustomLabel.Text text="Enable Gallery" />
              <CustomLabel.Popup popupContent="Publish approved videos to a public gallery where viewers can vote, like and comment. The gallery can only be enabled after the required legal details are complete." />
              <CustomLabel.Checkbox name="enableContest" checked={enableContest} onClick={handleCheckboxChange} />
            </CustomLabel>
          </Form.Field>

          {!canPublishGallery && (
            <Message warning size="small">
              Public gallery requires a contest organizer legal entity and Privacy Policy. Try enabling the gallery to
              see what needs to be completed.
            </Message>
          )}

          <Modal open={showLegalWarning} size="small" onClose={() => setShowLegalWarning(false)}>
            <Modal.Header>Complete legal details before publishing</Modal.Header>
            <Modal.Content>
              <p>
                Public Gallery can be enabled once the minimum legal requirements are complete. Add a contest organizer
                legal entity and Privacy Policy below, then Save.
              </p>
              <Message warning size="small">
                Required: contest organizer legal entity and Privacy Policy URL.
              </Message>
            </Modal.Content>
            <Modal.Actions>
              <Button onClick={() => setShowLegalWarning(false)}>OK</Button>
            </Modal.Actions>
          </Modal>

          {(enableContest || !canPublishGallery) && (
            <Segment
              style={{
                background: '#ffffff',
                border: '1px solid #e5e5e5',
                borderRadius: '12px',
                marginTop: '1rem',
                boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
              }}
            >
              <Header as="h4" style={{ margin: '0 0 0.5rem 0', fontWeight: 700 }}>
                Required legal details
              </Header>

              <p style={{ margin: '0 0 0.75rem 0', fontSize: '13px' }}>
                These identify the contest organizer responsible for the public gallery, voting and viewer interactions.
                They are required before the gallery can be published.
              </p>

              <Form.Input
                label="Contest organizer legal entity"
                name="overrideContentOwner"
                value={projectWithLegal.overrideContentOwner || legalEntity || ''}
                placeholder="Example: Vloggi Pty Ltd"
                onChange={handleLegalFieldChange}
              />
              <p style={{ fontSize: '12px', marginTop: '-0.5rem', color: '#444444' }}>
                The organization responsible for running the public gallery, voting and viewer interactions.
              </p>

              <Form.Input
                label="Privacy Policy URL"
                name="overridePrivacyUrl"
                value={projectWithLegal.overridePrivacyUrl || privacyPolicyUrl || ''}
                placeholder="Example: https://example.com/privacy"
                onChange={handleLegalFieldChange}
              />
              <p style={{ fontSize: '12px', marginTop: '-0.5rem', color: '#444444' }}>
                Viewers may interact with the gallery by voting, liking or commenting, so they need access to the
                organizer’s privacy information.
              </p>
            </Segment>
          )}

          {enableContest && (
            <Segment
              style={{
                width: '100%',
                backgroundColor: '#ffffff',
                border: '1px solid rgb(243, 244, 245)',
                borderRadius: '12px',
                marginTop: '1rem',
                padding: '1rem',
              }}
            >
              <Header as="h4" style={{ margin: '0 0 0.5rem 0', fontWeight: 700 }}>
                Share your gallery
              </Header>
              <p style={{ margin: '0 0 0.75rem 0', fontSize: '13px' }}>
                Use this link on social media, in email marketing, or on your website to send viewers directly to the
                public gallery.
              </p>
              <CollaborateTabContestLinks project={project} />
            </Segment>
          )}

          {enableContest && (
            <Form.Field required>
              <CustomLabel>
                <CustomLabel.Text text="Gallery welcome text" />
                <CustomLabel.Popup popupContent="Displayed above all videos on the public gallery. Use it to introduce the gallery, explain voting, and set expectations for viewers."></CustomLabel.Popup>
                <CustomLabel.WordCount
                  word={contestDescription}
                  limit={maxLength.contestDescription}
                ></CustomLabel.WordCount>
              </CustomLabel>

              <div
                style={{
                  background: '#f3f4f5',
                  border: '1px solid #d8d8d8',
                  borderRadius: '8px',
                  padding: '0.75rem',
                  margin: '0.5rem 0 0.75rem 0',
                  fontSize: '13px',
                  color: '#333333',
                }}
              >
                Write the short introduction that appears above the public gallery. Tell viewers what they are watching,
                how voting works and whether votes are final or advisory.
              </div>

              <Form.TextArea
                placeholder="Explore the shortlisted entries and cast your vote. Votes help identify audience favorites, but final selection may be moderated by the organizer."
                name="contestDescription"
                rows="6"
                maxLength={maxLength.contestDescription}
                value={contestDescription || ''}
                onChange={handleChange}
                error={
                  validationError === 'contestDescription' && {
                    content: `Please enter a welcome text for your project (max ${maxLength.contestDescription} characters)`,
                    pointing: 'above',
                  }
                }
              />
            </Form.Field>
          )}
          {enableContest && (
            <Segment
              style={{
                background: '#ffffff',
                border: '1px solid #e5e5e5',
                borderRadius: '12px',
                marginTop: '1rem',
                boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
              }}
            >
              <Header as="h4" style={{ margin: '0 0 0.5rem 0', fontWeight: 700 }}>
                Gallery rules and moderation
              </Header>

              <p style={{ margin: '0 0 0.75rem 0', fontSize: '13px' }}>
                These details help viewers understand how voting, comments and takedown requests are handled. They can
                be shown on the public gallery page. Use the Save button in the top right after editing.
              </p>

              <Form.Input
                label="Report / takedown contact email"
                name="contestContactEmail"
                value={contestContactEmail || ''}
                placeholder={
                  defaultContestContactEmail
                    ? `Default: ${defaultContestContactEmail}`
                    : 'Example: contests@example.com'
                }
                onChange={handleChange}
              />
              <p style={{ fontSize: '12px', marginTop: '-0.5rem', color: '#444444' }}>
                Used as the organizer contact for reported videos, comments, privacy concerns or takedown requests. If
                left blank, Vloggi will use the account email where available.
              </p>

              <Form.Select
                label="How should votes be used?"
                name="contestVotingUse"
                value={contestVotingUse || 'votes_help_shortlist'}
                options={[
                  { key: 'votes_help_shortlist', text: 'Votes help shortlist entries', value: 'votes_help_shortlist' },
                  { key: 'votes_decide_winner', text: 'Votes decide the winner', value: 'votes_decide_winner' },
                  {
                    key: 'votes_engagement_only',
                    text: 'Votes are for audience engagement only',
                    value: 'votes_engagement_only',
                  },
                ]}
                onChange={handleChange}
              />
              <p style={{ fontSize: '12px', marginTop: '-0.5rem', color: '#444444' }}>
                Default recommendation: votes help shortlist entries. This gives the organizer room to remove fake,
                duplicate or manipulated votes.
              </p>

              <Form.TextArea
                label="Voting rules"
                name="contestVotingRules"
                rows="3"
                value={contestVotingRules || ''}
                placeholder="Example: One vote per person. The organizer may remove suspicious, duplicate or manipulated votes."
                onChange={handleChange}
              />

              <Form.TextArea
                label="Commenting policy"
                name="contestCommentingPolicy"
                rows="3"
                value={contestCommentingPolicy || ''}
                placeholder="Example: Keep comments respectful and relevant. Abusive, spammy, private, unlawful or inappropriate comments may be removed."
                onChange={handleChange}
              />

              <Form.TextArea
                label="Moderation policy"
                name="contestModerationPolicy"
                rows="3"
                value={contestModerationPolicy || ''}
                placeholder="Example: The organizer may hide, remove or moderate videos, votes or comments that are reported or considered unsuitable."
                onChange={handleChange}
              />
            </Segment>
          )}
          {(enableContest || !canPublishGallery) && (
            <Segment
              style={{
                background: '#ffffff',
                border: '1px solid #e5e5e5',
                borderRadius: '12px',
                marginTop: '1rem',
                boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
              }}
            >
              <Header as="h4" style={{ margin: '0 0 0.5rem 0', fontWeight: 700 }}>
                Before you publish
              </Header>

              <p style={{ margin: '0 0 0.75rem 0', fontSize: '13px' }}>
                A public gallery needs a clear contest organizer and privacy notice before it can go live. Recommended
                items help viewers understand the rules, contact route and how votes or comments may be handled.
              </p>

              <Header as="h5" style={{ margin: '0.75rem 0 0.4rem 0', fontWeight: 700 }}>
                Required
              </Header>
              <GalleryLegalStatusRow label="Contest organizer" enabled={hasLegalEntity} />
              <GalleryLegalStatusRow label="Your Privacy Policy" enabled={hasPrivacyPolicy} />

              <Header as="h5" style={{ margin: '0.9rem 0 0.4rem 0', fontWeight: 700 }}>
                Recommended
              </Header>
              <GalleryLegalStatusRow label="Report / takedown route" enabled={hasReportOrTakedownRoute} />
              <GalleryLegalStatusRow label="Voting rules" enabled={!!contestVotingRules} />
              <GalleryLegalStatusRow label="Commenting policy" enabled={!!contestCommentingPolicy} />
              <GalleryLegalStatusRow label="Moderation policy" enabled={!!contestModerationPolicy} />
              <GalleryLegalStatusRow label="Your legal disclosure" enabled={hasLegalDisclosure} />
              <GalleryLegalStatusRow label="Gallery Terms & Conditions" enabled={hasGalleryTerms} />
              <GalleryLegalStatusRow label="Welcome text" enabled={!!contestDescription} />
              {!canPublishGallery && (
                <Message warning size="small" style={{ marginTop: '0.75rem' }}>
                  Complete the required legal details above, then Save. Once saved, the Enable Gallery slider can be
                  switched on.
                </Message>
              )}

              <Message size="small" style={{ marginTop: '0.75rem' }}>
                Good practice: publish approved videos only. If content is reported as illegal, private, abusive,
                copyrighted, unsafe, or unsuitable, hide it while you review.
              </Message>
            </Segment>
          )}
        </Form>
      </Segment>
    </>
  )
}

interface IProps extends RouteComponentProps<any> {
  project: IProject
  validationError: string | null
  handleChange: (arg: any, data: any) => void
}

export default withRouter(CollaborateTabContestPage)
